/**
 * Детерминированный генератор псевдослучайных чисел.
 *
 * Math.random непригоден: упражнение обязано воспроизводиться при заданном
 * seed, иначе property-based тесты и разбор жалоб пользователя невозможны
 * (ТЗ §16.2, инвариант 6).
 */

/** Источник псевдослучайности с фиксированным состоянием. */
export interface Rng {
  /** Следующее число в диапазоне [0, 1). */
  next(): number;
  /** Целое число в диапазоне [0, bound). */
  nextInt(bound: number): number;
  /** Случайный элемент непустого массива. */
  pick<T>(items: readonly T[]): T;
  /** Копия массива в случайном порядке. */
  shuffle<T>(items: readonly T[]): T[];
}

/** Хэширует строковый seed в 32-битное целое (алгоритм FNV-1a). */
export function hashSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * Создаёт генератор из строкового seed.
 *
 * Используется mulberry32: компактный, быстрый и с достаточным для учебных
 * задач качеством распределения.
 */
export function createRng(seed: string): Rng {
  let state = hashSeed(seed);

  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const nextInt = (bound: number): number => {
    if (bound <= 0) return 0;
    return Math.floor(next() * bound);
  };

  return {
    next,
    nextInt,
    pick<T>(items: readonly T[]): T {
      if (items.length === 0) throw new RangeError('Нельзя выбрать элемент из пустого массива');
      return items[nextInt(items.length)]!;
    },
    shuffle<T>(items: readonly T[]): T[] {
      const copy = [...items];
      for (let index = copy.length - 1; index > 0; index -= 1) {
        const swap = nextInt(index + 1);
        [copy[index], copy[swap]] = [copy[swap]!, copy[index]!];
      }
      return copy;
    },
  };
}
